import React, { useState } from 'react';
import { Button, Form, Input, Divider } from 'antd';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { enums, PUBLIC_HTTPS_URL } from '../../config';
import AuthLayout, { FormAlert, GoogleButton, responseCode, usePageTitle } from '../Login/AuthLayout';

// Server error codes, and the field each one is about (null: the whole form)
const SIGNUP_ERRORS = {
    [enums.errorCodes.c5]: ['email', 'inputErrors.emailExists'],
    [enums.errorCodes.c6]: ['username', 'inputErrors.usernameExists'],
    [enums.errorCodes.c11]: ['username', 'inputErrors.usernameReserved'],
    [enums.errorCodes.c12]: ['username', 'inputErrors.usernameInvalid'],
    [enums.errorCodes.c7]: ['password', 'inputErrors.passwordLeast4'],
    [enums.errorCodes.c8]: ['email', 'inputErrors.invalidEmail']
};

// Same rule the server applies (src/users.js validateUsername)
const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,30}$/;

const Page = () => {
    const [t] = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    usePageTitle(t('signup'));

    // The landing page's username field submits here as /signup?u=<handle>, so
    // whatever someone typed there is already filled in when they arrive.
    const prefilledUsername = (() => {
        try {
            const value = new URLSearchParams(window.location.search).get('u') || '';
            return value.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30);
        } catch (err) {
            return '';
        }
    })();

    const submit = (values) => {
        if (loading) return;
        setLoading(true);
        setError('');
        axios.post(`/users/register`, { username: values.username.trim(), password: values.password, email: values.email.trim() })
            .then(res => {
                localStorage.setItem('auth-token', res.data);
                window.location = '/admin';
            })
            .catch(err => {
                const known = SIGNUP_ERRORS[responseCode(err)];
                // Field problems go under the field they are about
                if (known) form.setFields([{ name: known[0], errors: [t(known[1])] }]);
                else setError(t('auth.genericError'));
            })
            .finally(() => setLoading(false));
    };

    const signupWithGoogle = (credentialResponse) => {
        if (loading) return;
        setLoading(true);
        setError('');
        axios.post(`/users/login/google`, { credential: credentialResponse.credential })
            .then(res => {
                localStorage.setItem('auth-token', res.data);
                window.location = '/admin';
            })
            .catch(() => setError(t('auth.googleFailed')))
            .finally(() => setLoading(false));
    };

    return (
        <AuthLayout
            title={t('auth.signupTitle')}
            subtitle={t('auth.signupSub')}
            footer={<>{t('auth.haveAccount')} <Link className="auth-link" to="/login">{t('login')}</Link></>}
        >
            <Form form={form} layout="vertical" requiredMark={false} size="large" onFinish={submit} className="normal_login_form"
                initialValues={{ username: prefilledUsername }}>
                <Form.Item name="username" label={t('username')}
                    rules={[
                        { required: true, whitespace: true, message: t('inputErrors.requiredThisField') },
                        { pattern: USERNAME_PATTERN, message: t('inputErrors.usernameInvalid') }
                    ]}>
                    {/* The username is the page address; show it as one */}
                    <Input prefix={<span className="auth-prefix">{PUBLIC_HTTPS_URL.replace(/^https?:\/\//, '')}/</span>}
                        autoFocus={!prefilledUsername} autoComplete="username" autoCapitalize="none" spellCheck={false} maxLength={30} />
                </Form.Item>
                <Form.Item name="email" label={t('email')}
                    rules={[
                        { required: true, whitespace: true, message: t('inputErrors.requiredThisField') },
                        { type: 'email', message: t('inputErrors.invalidEmail') }
                    ]}>
                    <Input type="email" autoFocus={!!prefilledUsername} autoComplete="email" autoCapitalize="none" spellCheck={false} />
                </Form.Item>
                <Form.Item name="password" label={t('password')} extra={t('auth.passwordHint')}
                    rules={[
                        { required: true, message: t('inputErrors.requiredThisField') },
                        { min: 4, message: t('inputErrors.passwordLeast4') }
                    ]}>
                    <Input.Password autoComplete="new-password" />
                </Form.Item>
                <FormAlert message={error} />
                <Button block loading={loading} type="primary" htmlType="submit" className="login-form-button">
                    {t('auth.createAccount')}
                </Button>
            </Form>
            <Divider plain className="auth-divider">{t('or')}</Divider>
            <GoogleButton text="signup_with" onCredential={signupWithGoogle} onError={() => setError(t('auth.googleFailed'))} />
        </AuthLayout>
    );
};

export default Page;
